import { Injectable } from '@angular/core';
import { Observable, map } from 'rxjs';
import { BaseService } from '../../abstract/base.service';
import { DroneSource } from '../data-models/drone-source.model';
import { Organization } from '../data-models/organization.model';
import { OrganizationMembershipSummary } from '../data-models/organization-membership-summary.model';
import { GrantMembershipRequest } from '../request-models/grant-membership-request.model';
import { OrganizationRequest } from '../request-models/organization-request.model';
import { OrganizationDrone } from '../response-models/organization-drone.model';
import { OrganizationDronesResponse } from '../response-models/organization-drones-response.model';
import { OrganizationOption } from '../response-models/organization-option.model';
import { OrganizationTabCounts } from '../response-models/organization-tab-counts.model';
import { Coordinates } from '../response-models/coordinates.model';
import { Page } from '../response-models/page.model';
import { toCoordinates, toCoordinatesRequest } from '../utils/coordinates-utils';
import { isRecord } from '../utils/is-record';
import { toOrganizationRequest } from '../utils/organization-request-utils';
import { toMembershipSummary } from '../utils/organization-wire-utils';
import { countOrZero, dateOrNull, idString, nullableString, stringArrayOrEmpty } from '../utils/wire-value-utils';

/** The hub clamps `/admin/organizations` lookups at this page size (`PagingModel.MAX_LOOKUP_PAGE_SIZE`). */
export const ORGANIZATION_OPTIONS_PAGE_SIZE = 1000;

/** Query for the organizations list. Blank values are not sent. */
export interface OrganizationListQuery {
  page?: number;
  pageSize?: number;
  search?: string;
  sortBy?: string;
  sortOrder?: 'asc' | 'desc';
  organizationType?: string;
  businessType?: string;
  includeArchived?: boolean;
}

/**
 * ADN organizations: `/admin/organizations` (hub `admin-organizations-controller.ts`), paged with `pageSize`.
 * Also the org drones (hub `admin-drone-assignments-controller.ts`), tab counts, archive / unarchive / delete,
 * complimentary membership grant / revoke and report states. Org memberships, invitations and data tabs have
 * their own services.
 */
@Injectable({ providedIn: 'root' })
export class OrganizationService extends BaseService<Organization> {
  constructor() {
    super('admin/organizations', Organization);
  }

  /** GET /admin/organizations: one page of organizations (list fields only). */
  getOrganizations(query: OrganizationListQuery = {}): Observable<Page<Organization>> {
    return this.getPage({ query: toListQuery(query) });
  }

  /**
   * Every organization as `{ id, name }`, for org pickers and report filters: one call at the hub's lookup
   * clamp (1000). Archived organizations are left out, as in the default list.
   */
  getOptions(): Observable<Page<OrganizationOption>> {
    const query = { page: 1, pageSize: ORGANIZATION_OPTIONS_PAGE_SIZE, sortBy: 'name', sortOrder: 'asc' };
    return this.getPage({ query }).pipe(
      map(page => ({ items: page.items.map(org => ({ id: org.id, name: org.name ?? '' })), paging: page.paging }))
    );
  }

  /** GET /admin/organizations/:id, with the membership summary and payment accounts. */
  getOrganization(organizationId: string): Observable<Organization | null> {
    return this.get(organizationId);
  }

  /** PUT /admin/organizations/:id with only the writable fields that are set. */
  updateOrganization(organizationId: string, fields: OrganizationRequest): Observable<Organization | null> {
    return this.update(organizationId, toOrganizationRequest(fields));
  }

  /** Makes `newOwnerProfileId` (an active member) the owner; the old owner keeps `oldOwnerRole` when given. */
  transferOwnership(organizationId: string, newOwnerProfileId: string, oldOwnerRole?: number): Observable<Organization | null> {
    return this.updateOrganization(organizationId, { ownerProfileId: newOwnerProfileId, oldOwnerRole });
  }

  /** Sets the Chief Supervisor (an active member), or removes it with `null`. */
  assignChiefSupervisor(organizationId: string, profileId: string | null): Observable<Organization | null> {
    return this.updateOrganization(organizationId, { chiefSupervisorProfileId: profileId });
  }

  /** GET /admin/organizations/:id/tab-counts */
  getTabCounts(organizationId: string): Observable<OrganizationTabCounts> {
    return this.request('GET', { id: organizationId, path: 'tab-counts' }, toTabCounts);
  }

  /** POST /admin/organizations/:id/archive */
  archive(organizationId: string): Observable<void> {
    return this.action(organizationId, 'archive');
  }

  /** POST /admin/organizations/:id/unarchive */
  unarchive(organizationId: string): Observable<void> {
    return this.action(organizationId, 'unarchive');
  }

  /** POST /admin/organizations/:id/delete (soft delete). */
  softDelete(organizationId: string): Observable<void> {
    return this.action(organizationId, 'delete');
  }

  /** POST /admin/organizations/:id/membership/grant: a complimentary Pro membership until `expiresAt`. */
  grantMembership(organizationId: string, body: GrantMembershipRequest): Observable<OrganizationMembershipSummary | null> {
    const request = { expiresAt: body.expiresAt, reason: body.reason };
    return this.request('POST', { id: organizationId, path: ['membership', 'grant'] }, toSummaryOrNull, request);
  }

  /** POST /admin/organizations/:id/membership/revoke: ends a complimentary Pro membership now. */
  revokeMembership(organizationId: string): Observable<OrganizationMembershipSummary | null> {
    return this.request('POST', { id: organizationId, path: ['membership', 'revoke'] }, toSummaryOrNull, {});
  }

  /** GET /admin/organizations/:id/report-states: the 2-letter states of the organization's fields. */
  getReportStates(organizationId: string): Observable<string[]> {
    return this.request('GET', { id: organizationId, path: 'report-states' }, stringArrayOrEmpty);
  }

  /** GET /admin/organizations/:id/drones. With `includeHistory`, `adnAssigned` also holds past assignments. */
  getDrones(organizationId: string, includeHistory: boolean = false): Observable<OrganizationDronesResponse> {
    const query = includeHistory ? { includeHistory: true } : undefined;
    return this.request('GET', { id: organizationId, path: 'drones', query }, toOrganizationDrones);
  }

  /** GET /admin/organizations/:id/geocode: coordinates for the saved address, or null if the hub sent none. */
  geocode(organizationId: string): Observable<Coordinates | null> {
    return this.request('GET', { id: organizationId, path: 'geocode' }, toCoordinates);
  }

  /** PUT /admin/organizations/:id/coordinates (not writable through the organization PUT). */
  updateCoordinates(organizationId: string, coords: Coordinates): Observable<void> {
    return this.request('PUT', { id: organizationId, path: 'coordinates' }, () => undefined, toCoordinatesRequest(coords));
  }

  private action(organizationId: string, path: string): Observable<void> {
    return this.request('POST', { id: organizationId, path }, () => undefined, {});
  }
}

function toListQuery(query: OrganizationListQuery): Record<string, string | number | boolean | undefined> {
  return {
    page: query.page,
    pageSize: query.pageSize,
    search: query.search?.trim() || undefined,
    sortBy: query.sortBy || undefined,
    sortOrder: query.sortOrder,
    organizationType: query.organizationType || undefined,
    businessType: query.businessType || undefined,
    includeArchived: query.includeArchived || undefined,
  };
}

function toTabCounts(data: unknown): OrganizationTabCounts {
  const source = isRecord(data) ? data : {};
  return {
    members: countOrZero(source['members']),
    drones: countOrZero(source['drones']),
    certifications: countOrZero(source['certifications']),
    sprayRecords: countOrZero(source['sprayRecords']),
    customers: countOrZero(source['customers']),
    safetyTestResults: countOrZero(source['safetyTestResults']),
  };
}

function toSummaryOrNull(data: unknown): OrganizationMembershipSummary | null {
  return toMembershipSummary(data) ?? null;
}

function toOrganizationDrones(data: unknown): OrganizationDronesResponse {
  const source = isRecord(data) ? data : {};
  return { adnAssigned: toDroneList(source['adnAssigned']), organizationOwned: toDroneList(source['organizationOwned']) };
}

function toDroneList(value: unknown): OrganizationDrone[] {
  return Array.isArray(value) ? value.filter(isRecord).map(toOrganizationDrone) : [];
}

/** Copies the named fields of one flattened org drone (never the record itself). */
function toOrganizationDrone(source: Record<string, unknown>): OrganizationDrone {
  const drone: OrganizationDrone = {
    id: idString(source['id']),
    serialNumber: nullableString(source['serialNumber']) ?? '',
    name: nullableString(source['name']) ?? '',
    nNumber: nullableString(source['nNumber']) ?? null,
    manufacturerName: nullableString(source['manufacturerName']) ?? null,
    modelName: nullableString(source['modelName']) ?? null,
    status: nullableString(source['status']) ?? '',
    source: (source['source'] === 'organization' ? 'organization' : 'adn') as DroneSource,
  };
  const assignmentId = nullableString(source['assignmentId']);
  if (assignmentId) drone.assignmentId = assignmentId;
  const assignedAt = dateOrNull(source['assignedAt']);
  if (assignedAt) drone.assignedAt = assignedAt;
  const createdAt = dateOrNull(source['createdAt']);
  if (createdAt) drone.createdAt = createdAt;
  if ('lastUsed' in source) drone.lastUsed = dateOrNull(source['lastUsed']);
  return drone;
}
